import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { Appointment } from '../types';
import { RecordingState } from '../types/state';
import { ContextData } from '../types/widget';

type StopRecordingFn = (
  onComplete?: (success: boolean) => void
) => Promise<unknown>;

type AbortRecordingFn = () => Promise<void>;
type StartFreshUnifiedSessionFn = () => Promise<string>;

const initialState = {
  isRecording: false,
  isPaused: false,
  activeRecordingSession: null,
  whisperSessionId: null,
  recordingSessionId: null,
  pendingEncounter: null,
  pendingContextData: null,
  transcriptSaved: false,
  hasRecordedInSession: false,

  isGeneratingDocument: false,
  unifiedSessionIdEnabled: false,

  completedDurations: {} as Record<string, string>,

  savedTranscriptData: null as {
    segments: Array<{
      id: string;
      timestamp: string;
      text: string;
      speaker: string;
      confidence: number;
    }>;
    transcriptText: string;
  } | null,

  stopRecordingFn: null as StopRecordingFn | null,
  abortRecordingFn: null as AbortRecordingFn | null,
  startFreshUnifiedSessionFn: null as StartFreshUnifiedSessionFn | null,
};

export const useRecordingStore = create<RecordingState>()(
  devtools(
    (set, get) => ({
      isRecording: false,
      isPaused: false,
      activeRecordingSession: null,
      whisperSessionId: null,
      recordingSessionId: null,
      pendingEncounter: null,
      pendingContextData: null,
      transcriptSaved: false,
      hasRecordedInSession: false,
      isGeneratingDocument: false,
      unifiedSessionIdEnabled: false,
      completedDurations: {},
      savedTranscriptData: null,
      stopRecordingFn: null,
      abortRecordingFn: null,
      startFreshUnifiedSessionFn: null,

      setIsRecording: (isRecording: boolean) =>
        set({ isRecording }, false, 'recording/setIsRecording'),

      setIsPaused: (isPaused: boolean) =>
        set({ isPaused }, false, 'recording/setIsPaused'),

      setActiveRecordingSession: (activeRecordingSession: Appointment | null) =>
        set(
          { activeRecordingSession },
          false,
          'recording/setActiveRecordingSession'
        ),

      setWhisperSessionId: (whisperSessionId: string | null) =>
        set({ whisperSessionId }, false, 'recording/setWhisperSessionId'),

      setRecordingSessionId: (recordingSessionId: string | null) =>
        set({ recordingSessionId }, false, 'recording/setRecordingSessionId'),

      setPendingEncounter: (pendingEncounter: Appointment | null) =>
        set({ pendingEncounter }, false, 'recording/setPendingEncounter'),

      setPendingContextData: (
        pendingContextData: {
          appointmentId: string;
          contextData: ContextData;
        } | null
      ) =>
        set({ pendingContextData }, false, 'recording/setPendingContextData'),

      setTranscriptSaved: (transcriptSaved: boolean) =>
        set({ transcriptSaved }, false, 'recording/setTranscriptSaved'),

      setHasRecordedInSession: (hasRecordedInSession: boolean) =>
        set(
          { hasRecordedInSession },
          false,
          'recording/setHasRecordedInSession'
        ),

      setIsGeneratingDocument: (isGeneratingDocument: boolean) =>
        set(
          { isGeneratingDocument },
          false,
          'recording/setIsGeneratingDocument'
        ),

      setUnifiedSessionIdEnabled: (unifiedSessionIdEnabled: boolean) =>
        set(
          { unifiedSessionIdEnabled },
          false,
          'recording/setUnifiedSessionIdEnabled'
        ),

      setSavedTranscriptData: (
        data: {
          segments: Array<{
            id: string;
            timestamp: string;
            text: string;
            speaker: string;
            confidence: number;
          }>;
          transcriptText: string;
        } | null
      ) =>
        set(
          { savedTranscriptData: data },
          false,
          'recording/setSavedTranscriptData'
        ),

      clearActiveRecording: () => {
        set(
          (state) => ({
            activeRecordingSession: null,
            isRecording: false,
            isPaused: false,
            whisperSessionId: null,
            recordingSessionId: null,
            transcriptSaved: false,
            hasRecordedInSession: false,
            isGeneratingDocument: state.isGeneratingDocument,
          }),
          false,
          'recording/clearActiveRecording'
        );
      },

      setCompletedDuration: (sessionId: string, duration: string) =>
        set(
          (state) => ({
            completedDurations: {
              ...state.completedDurations,
              [sessionId]: duration,
            },
          }),
          false,
          'recording/setCompletedDuration'
        ),

      setStopRecordingFn: (stopRecordingFn: StopRecordingFn | null) =>
        set({ stopRecordingFn }, false, 'recording/setStopRecordingFn'),

      callStopRecording: async (onComplete?: (success: boolean) => void) => {
        const { stopRecordingFn } = get();
        if (stopRecordingFn) {
          return stopRecordingFn(onComplete);
        } else {
          throw new Error('No stopRecording function available');
        }
      },

      setAbortRecordingFn: (abortRecordingFn: AbortRecordingFn | null) =>
        set({ abortRecordingFn }, false, 'recording/setAbortRecordingFn'),

      callAbortRecording: async () => {
        const { abortRecordingFn } = get();
        if (!abortRecordingFn) {
          throw new Error('No abortRecording function available');
        }
        await abortRecordingFn();
      },

      setStartFreshUnifiedSessionFn: (
        startFreshUnifiedSessionFn: StartFreshUnifiedSessionFn | null
      ) =>
        set(
          { startFreshUnifiedSessionFn },
          false,
          'recording/setStartFreshUnifiedSessionFn'
        ),

      callStartFreshUnifiedSession: async () => {
        const { startFreshUnifiedSessionFn } = get();
        if (!startFreshUnifiedSessionFn) {
          throw new Error('No startFreshUnifiedSession function available');
        }
        return startFreshUnifiedSessionFn();
      },

      resetStore: () => {
        set(
          {
            ...initialState,
            stopRecordingFn: null,
            abortRecordingFn: null,
            startFreshUnifiedSessionFn: null,
          },
          false,
          'recording/resetStore'
        );
      },
    }),
    { name: 'recording-store' }
  )
);

export const useRecordingState = () => {
  const isRecording = useRecordingStore((state) => state.isRecording);
  const isPaused = useRecordingStore((state) => state.isPaused);
  const activeRecordingSession = useRecordingStore(
    (state) => state.activeRecordingSession
  );

  return {
    isRecording,
    isPaused,
    activeRecordingSession,
  };
};

export const useSessionState = () => {
  const activeRecordingSession = useRecordingStore(
    (state) => state.activeRecordingSession
  );
  const whisperSessionId = useRecordingStore((state) => state.whisperSessionId);
  const recordingSessionId = useRecordingStore(
    (state) => state.recordingSessionId
  );
  const pendingEncounter = useRecordingStore((state) => state.pendingEncounter);

  return {
    activeRecordingSession,
    whisperSessionId,
    recordingSessionId,
    pendingEncounter,
  };
};
